// ══════════════════════════════════════════════════════════════════════
// Editable PDF print data — schemas + invoice-derived defaults
//
// Generalises the Truck Receipt flow to every generated document: each PDF
// type lists the fields printed on it and a buildDefaults() that derives
// them from the invoice/company exactly like the generator used to do
// inline. Saved edits (commercial_invoice_pdf_data) win field-by-field;
// anything the user never touched keeps following the invoice.
//
// The same resolved object drives both the edit form and the PDF, so what
// you see in the form is what prints.
// ══════════════════════════════════════════════════════════════════════
import {
  formatDate, numberToWords, amountWordsCore, amountInWords, sv, getItems, getTrBoe,
  buildLcRefLine, buildProformaRef, buildRegistrationRef, buildCoverNoteRefLine,
  buildDespatchRef, buildPackagesLine,
} from './helpers';

const usd = (v, maxFrac = 3) =>
  parseFloat(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: maxFrac });

const longToday = () => {
  const d = new Date();
  return `${d.toLocaleDateString('en-GB', { month: 'long' })} ${d.getDate()}, ${d.getFullYear()}`;
};

const netWeight = (document, items) =>
  document.total_net_weight || items.reduce((s, i) => s + (parseFloat(i.net_weight_kg) || 0), 0);
const grossWeight = (document, items) =>
  document.total_gross_weight || items.reduce((s, i) => s + (parseFloat(i.gross_weight_kg) || 0), 0);

// Shared field shorthands
const t = (key, label) => ({ key, label });
const ta = (key, label, rows = 3) => ({ key, label, type: 'textarea', rows });
// A block the invoice writes and the form only shows: it is rebuilt from the
// invoice on every print, so the editor offers no box for it and a saved copy
// of it never overrides the invoice.
const fx = (key, label, rows = 5) => ({ key, label, type: 'textarea', rows, fixed: true });
// A block the exporter writes: the invoice only supplies the opening wording,
// so an emptied box prints as nothing instead of falling back to it.
const tw = (key, label, rows = 3) => ({ key, label, type: 'textarea', rows, own: true });

// ── Shared builders ─────────────────────────────────────────────────────

// Bill of Exchange (First / Second share every field except the paragraph)
function buildBillOfExchangeDefaults(document, company, which) {
  const totalUsd = parseFloat(document.total_usd) || 0;
  // Bare words only — the paragraph supplies "US DOLLAR ... ONLY" itself.
  const amountWords = amountWordsCore(sv(document.total_in_words, numberToWords(totalUsd)));

  const lcOpeningBank = sv(document.lc_opening_bank || document.advising_bank, 'BANK NAME');
  const lcOpeningBankAddr = sv(document.lc_opening_bank_address, '');

  let drawnOnText = `DRAWN ON ${lcOpeningBank.toUpperCase()}`;
  if (lcOpeningBankAddr) drawnOnText += `, ${lcOpeningBankAddr.toUpperCase()}`;
  drawnOnText += ` UNDER LETTER OF CREDIT NO. ${sv(document.lc_no)}`;

  // companies.negotiation_bank already holds name + branch + address in one
  // field, so nothing else may be appended to it.
  const negotiationBank = sv(company.negotiation_bank, 'UNION BANK OF INDIA, OVERSEAS BRANCH');
  const ordinal = which === 'second'
    ? 'SECOND OF EXCHANGE (FIRST OF THE SAME DATE AND TENOR REMAINING UNPAID)'
    : 'FIRST OF EXCHANGE (SECOND OF THE SAME DATE AND TENOR REMAINING UNPAID)';

  return {
    date_line: longToday(),
    drawn_on: drawnOnText,
    dated_line: `DATED : ${formatDate(document.lc_opening_date)}`,
    ref_line: `REF. NOS. ${sv(document.invoice_no)} DATE: ${formatDate(document.document_date)}`,
    exchange_line: `EXCHANGE FOR USD ${usd(totalUsd)}`,
    main_text: `AT LC AT SIGHT , PLEASE PAY THIS ${ordinal} TO THE ORDER OF ${negotiationBank.toUpperCase()}. THE SUM OF US DOLLAR ${amountWords.toUpperCase()} ONLY BEING THE VALUE RECEIVED AND CHARGE THE SAME TO INVOICE NOS. ${sv(document.invoice_no)} DATE: ${formatDate(document.document_date)}.`,
    to_block: lcOpeningBankAddr
      ? `${lcOpeningBank.toUpperCase()},\n${lcOpeningBankAddr.toUpperCase()}`
      : lcOpeningBank.toUpperCase(),
    ac_of_block: `${sv(document.buyer_name).toUpperCase()}\n${sv(document.buyer_address).toUpperCase()}`,
  };
}

const BILL_FIELDS = [
  t('date_line', 'Date Line'),
  ta('drawn_on', 'Drawn On (bank / L/C line)', 2),
  t('dated_line', 'L/C Dated Line'),
  t('ref_line', 'Ref. Nos. Line'),
  t('exchange_line', 'Exchange For Line'),
  ta('main_text', 'Main Declaration Paragraph', 4),
  ta('to_block', 'TO (bank block)', 2),
  ta('ac_of_block', 'A/C OF (buyer block)', 2),
  t('signatory', 'Signatory'),
];

// Certificates printed onto pre-printed ABC International stationery
// (Phytosanitary / Radioactivity / Health) share the same field layout.
function buildAbcCertificateDefaults(document, company, declaration) {
  const items = getItems(document);
  const commodity = items[0] ? sv(items[0].description_of_goods) : sv(document.commodity, 'Export Goods');
  const netWt = netWeight(document, items);

  return {
    date_line: formatDate(document.document_date),
    exporter_block: `${sv(company.exporter_name).toUpperCase()}\n${sv(company.exporter_address).toUpperCase()}\nMAIL:${sv(company.email)}, GSTIN: ${sv(company.gstin)}`,
    buyer_block: `${sv(document.buyer_name).toUpperCase()}\n${sv(document.buyer_address).toUpperCase()}`,
    lc_text: `L/C NO. ${sv(document.lc_no)} DATE ${formatDate(document.lc_opening_date)} ISSUED BY ${sv(document.lc_opening_bank).toUpperCase()}, ${sv(document.lc_opening_bank_address, '').toUpperCase()}`,
    proforma_line: `PROFORMA INVOICE NO.${sv(document.proforma_invoice_no)} DATED: ${formatDate(document.proforma_invoice_date)}`,
    invoice_line: `${sv(document.invoice_no)} DATED: ${formatDate(document.document_date)}`,
    goods_line: commodity.toUpperCase(),
    hs_code: sv(document.hs_code),
    weight_packing: `${parseFloat(netWt).toFixed(3)} KGS. / LOOSE`,
    quality_line: 'EXPORT STANDARD QUALITY.',
    packing_line: 'EXPORT STANDARD PACKING.',
    declaration,
  };
}

const ABC_CERT_FIELDS = [
  t('date_line', 'Date'),
  ta('exporter_block', 'Exporter Block', 3),
  ta('buyer_block', 'Consignee / Buyer Block', 2),
  ta('lc_text', 'L/C Details', 2),
  t('proforma_line', 'Proforma Invoice Line'),
  t('invoice_line', 'Invoice No. & Date Line'),
  ta('goods_line', 'Description of Goods', 2),
  t('hs_code', 'H.S. Code'),
  t('weight_packing', 'Net Weight / Packing'),
  t('quality_line', 'Quality Line'),
  t('packing_line', 'Packing Line'),
  ta('declaration', 'Bottom Declaration', 4),
];

// Shipment advice family (opening bank / insurance co. / buyer)
function buildShipmentAdviceCommon(document, company) {
  const trBoe = getTrBoe(document);
  const items = getItems(document);
  const totalUsd = parseFloat(document.total_usd || 0);
  const commodity = items[0] ? sv(items[0].description_of_goods) : sv(document.commodity, 'Export Goods');
  const netWt = netWeight(document, items);
  const grossWt = grossWeight(document, items);

  return {
    date_line: `DATE: ${formatDate(document.document_date)}`,
    // Every copy of the advice quotes the same reference block and opens with
    // the same paragraph; only the addressee above them differs.
    ref_line1: buildLcRefLine(document),
    ref_line2: buildShipmentAdviceRefLine2(document, company),
    ref_line3: buildCoverNoteRefLine(document),
    body_text: 'WE HEREBY GIVING FULL DETAILS OF SHIPMENT PERTAINING TO THE SHIPMENT MADE UNDER ABOVE REF FOR INSURANCE PURPOSE. THE DETAILS ARE AS FOLLOWS:',
    buyer_block: `${sv(document.buyer_name).toUpperCase()}\n${sv(document.buyer_address).toUpperCase()}`,
    invoice_line: `${sv(document.invoice_no)} DATE: ${formatDate(document.document_date)}`,
    invoice_value: `USD ${usd(totalUsd)}`,
    // "AMINO RESIN (LIQUID) - 26000.000 KGS." — the shipped quantity belongs
    // on the description row, not a trailing comma with nothing after it.
    goods_line: `${commodity.toUpperCase()}${netWt ? ` - ${parseFloat(netWt).toFixed(3)} KGS.` : ''}`,
    weight_line: `${parseFloat(netWt).toFixed(3)} KGS. & ${parseFloat(grossWt).toFixed(3)} KGS.`,
    despatch_line: `${sv(trBoe.despatch_doc_no)} DATE : ${formatDate(trBoe.despatch_date)}`,
    carrier: sv(trBoe.transporter_name || trBoe.carrier).toUpperCase(),
    closing_line: 'THE ABOVE DETAILS HAS BEEN SENT TO YOU IMMEDIATELY AFTER SHIPMENT.',
  };
}

// Ref line 2 of the shipment advice family: the linked proforma, the
// importer's registration numbers and the H.S. code. Every copy — opening
// bank, insurer and buyer alike — quotes all three. Each part is read from
// the field that actually holds it and dropped when that field is empty, so
// the line never prints a bare "DATED:" or "IRC NO." with nothing behind it.
function buildShipmentAdviceRefLine2(document, company) {
  const parts = [buildProformaRef(document), buildRegistrationRef(document, company)];
  if (sv(document.hs_code).trim()) parts.push(`H.S. CODE NO. ${sv(document.hs_code).trim()}`);
  return parts.filter(Boolean).join(' ');
}

// The closing reference of the shipment notices: the goods' H.S. code and the
// importer's own registration numbers. The numbers are read from the line the
// invoice carries in Declaration / Other Reference — they belong to the
// importer, so the exporter's own IRC/TIN/BIN columns are only the fallback
// inside buildRegistrationRef. A part the invoice has no field for is dropped
// rather than printed as a bare label.
function buildHsRegistrationLine(document, company) {
  const hsCode = sv(document.hs_code).trim();
  return [hsCode && `H.S. CODE : ${hsCode}`, buildRegistrationRef(document, company)]
    .filter(Boolean).join(' ');
}

// Shipment advice to the L/C opening bank. It is a different letter from the
// insurer's and the buyer's copies: the bank opened the credit, so it is told
// what actually moved — the carrier, the amount in words, the commodity, the
// weights and the packages — with the credit as the only reference above the
// rows, and the H.S. code and the importer's registration numbers under them.
function buildShipmentAdviceBankDefaults(document, company) {
  const trBoe = getTrBoe(document);
  const items = getItems(document);
  const totalUsd = parseFloat(document.total_usd || 0);
  const netWt = netWeight(document, items);
  const grossWt = grossWeight(document, items);
  const commodity = items[0] ? sv(items[0].description_of_goods) : sv(document.commodity, 'Export Goods');
  const quantity = items[0] ? `${parseFloat(items[0].quantity || 0).toFixed(4)} ${sv(items[0].uom).toUpperCase()}` : '';
  // Every line item's packages added up against the one fixed unit: "8 PKGS"
  const packagesLine = buildPackagesLine(document, items) || 'LOOSE';
  const despatch = buildDespatchRef(trBoe);
  const shipmentDate = formatDate(trBoe.despatch_date || document.document_date);
  // The credit is the whole reference — the bank is being written to about
  // its own L/C — and it stands alone here, so it keeps no trailing comma.
  const lcRef = buildLcRefLine(document, { dateLabel: 'DATE', separator: ' ' }).replace(/,\s*$/, '');

  return {
    date_line: `DATE: ${formatDate(document.document_date)}`,
    to_block: `${sv(document.lc_opening_bank).toUpperCase()},\n${sv(document.lc_opening_bank_address, '').toUpperCase()}`,
    ref_text: lcRef ? `REF. NO.: ${lcRef}` : '',
    body_text: `KINDLY NOTE THAT WE HAVE EFFECTED THE FOLLOWING SHIPMENT ${buildShipmentRoute(document)} AS PER THE FOLLOWING DETAILS :-`,
    carrier_line: despatch ? `${shipmentDate} (AS PER ${despatch})` : shipmentDate,
    buyer_block: `${sv(document.buyer_name).toUpperCase()} ${sv(document.buyer_address).toUpperCase()}`,
    invoice_line: `${sv(document.invoice_no)} DATE: ${formatDate(document.document_date)}`,
    amount_line: `USD. ${usd(totalUsd)} (${amountInWords(document, totalUsd).toUpperCase()})`,
    commodity_line: quantity ? `${commodity.toUpperCase()}, QUANTITY : ${quantity}` : commodity.toUpperCase(),
    net_weight_line: `NET WEIGHT : ${parseFloat(netWt).toFixed(3)} KGS. AND`,
    gross_weight_line: `GROSS WEIGHT : ${parseFloat(grossWt).toFixed(3)} KGS.`,
    packages_line: packagesLine,
    other_ref: buildHsRegistrationLine(document, company),
  };
}

// "FROM PETRAPOLE, INDIA TO BENAPOLE PORT, BANGLADESH BY ROAD/BY TRUCK" —
// each leg read from the field that holds it and dropped when that field is
// empty, so the sentence never reads "FROM  TO ".
function buildShipmentRoute(document) {
  const from = sv(document.shipment_from).trim().toUpperCase();
  const to = sv(document.shipment_to).trim().toUpperCase();
  const mode = sv(document.mode_of_shipment).trim().toUpperCase();
  // A road consignment is described "BY ROAD/BY TRUCK" on every advice; any
  // other mode is named the way the invoice has it.
  const movement = !mode || /ROAD|TRUCK/.test(mode) ? 'BY ROAD/BY TRUCK' : `BY ${mode}`;
  return [from && `FROM ${from}`, to && `TO ${to}`, movement].filter(Boolean).join(' ');
}

// The RE block of the non-negotiable / truck-operator letter: the credit, the
// invoice it covers, the importer's registration numbers and the linked
// proforma. Registration numbers are read out of the one line that holds them
// inside Declaration / Other Reference, never by pasting the whole block in —
// the LUT and country-of-origin paragraphs around it are invoice wording. Any
// part whose field is empty is dropped rather than printed as a bare label.
function buildNonNegotiableRef(document, company) {
  const invoiceNo = sv(document.invoice_no).trim();
  const parts = [
    buildLcRefLine(document, { label: 'LETTER OF CREDIT NO.', dateLabel: 'DATE:' }),
    invoiceNo && `INVOICE NO. ${invoiceNo}` +
      (document.document_date ? ` DATE: ${formatDate(document.document_date)}` : ''),
    buildRegistrationRef(document, company),
  ];
  // buildLcRefLine ends in a comma when it stands alone as a line of its own;
  // here it runs on into the invoice, so the joiner supplies the separators.
  const head = parts.filter(Boolean).map(part => part.replace(/,\s*$/, '')).join(', ');
  const proforma = buildProformaRef(document);
  return [head, proforma].filter(Boolean).join(' ');
}

// The letter the L/C opening bank is sent: the same rows are typed whether it
// goes out as a fax transmission or as a shipment advice.
const SHIPMENT_NOTICE_FIELDS = [
  t('date_line', 'Date Line'),
  ta('to_block', 'TO (bank block)', 2),
  ta('ref_text', 'REF. NO. Line', 2),
  ta('body_text', 'Body Paragraph', 2),
  ta('carrier_line', '01. Shipment Date and Carrier Details', 2),
  ta('buyer_block', '02. Consignee / Buyer / Applicant', 2),
  t('invoice_line', '03. Invoice No. & Date'),
  ta('amount_line', '04. Amount of Bill', 2),
  t('commodity_line', '05. Commodity and Quantity'),
  t('net_weight_line', '06a. Net Weight'),
  t('gross_weight_line', '06b. Gross Weight'),
  t('packages_line', '07. Total Packages'),
  ta('other_ref', 'Other Ref (full width)', 2),
];

const SHIPMENT_ADVICE_FIELDS = [
  t('date_line', 'Date Line'),
  ta('to_block', 'TO (recipient block)', 2),
  t('ref_line1', 'Ref Line 1 (L/C)'),
  ta('ref_line2', 'Ref Line 2', 2),
  ta('ref_line3', 'Ref Line 3 (cover note)', 2),
  ta('body_text', 'Body Paragraph', 2),
  ta('buyer_block', '01. Consignee / Buyer', 2),
  t('invoice_line', '02. Invoice No. & Date'),
  t('invoice_value', '03. Invoice Value'),
  ta('goods_line', '04. Description of Goods', 2),
  t('weight_line', '05. Net & Gross Weight'),
  t('despatch_line', '06. Despatch Document No. & Date'),
  ta('carrier', '07. Carrier', 2),
  t('closing_line', 'Closing Statement'),
];

// ══════════════════════════════════════════════════════════════════════
// Schemas
// ══════════════════════════════════════════════════════════════════════

// The clause the carrier certifies on its "TO WHOMSOEVER IT MAY CONCERN"
// letter. Bangladeshi credits ask that the consignment be moved only by
// carriers honouring the flag, port and transhipment restrictions the
// importing country enforces, and this is the wording the carrier issues it
// in.
const CARRIER_CERTIFICATION =
  'IS EFFECTED BY COMPANIES HONOURING BANGLADESH LAWS AND REGULATIONS REGARDING '
  + 'VESSELS FLAGS, CARRIER FLAGS, PORTS, SHIPMENT AND TRANSHIPMENT';

export const PDF_EDIT_SCHEMAS = {
  // ── Certificate of Origin ───────────────────────────────────────────
  certificate: {
    title: 'Certificate of Origin',
    icon: '📜',
    fields: [
      t('exporter_name', 'Exporter Name'),
      ta('exporter_details', 'Exporter Address / Details', 2),
      t('buyer_name', 'Buyer Name'),
      ta('buyer_details', 'Buyer Address', 2),
      t('transport_mode', 'Transport Mode'),
      t('place_of_origin', 'Place of Origin'),
      t('port_of_discharge', 'Port of Discharge'),
      t('final_destination', 'Final Destination'),
      t('gross_weight', 'Gross Weight'),
      t('packing_type', 'Packing Type'),
      ta('goods_desc', 'Goods Description', 2),
      t('net_weight', 'Net Weight'),
      t('cert_line', 'Certification Line'),
      t('usd_value', 'USD Value'),
      ta('tin_line', 'TIN / VAT / IRC / BIN / HS Line', 2),
      ta('notify', 'Notify', 3),
      t('sb_no', 'S.B. No.'),
      t('sb_date', 'S.B. Date'),
      t('lc_no', 'L/C No.'),
      t('lc_date', 'L/C Date'),
      t('invoice_no', 'Invoice No.'),
      t('invoice_date', 'Invoice Date'),
      t('proforma_line', 'Proforma Reference Line'),
      t('iec_line', 'IEC Line'),
      t('signatory', 'Signatory'),
    ],
    buildDefaults(document, company, authority) {
      const trBoe = getTrBoe(document);
      const items = getItems(document);
      const totalUsd = parseFloat(document.total_usd || 0);
      const netWt = parseFloat(document.total_net_weight || 0) ||
        items.reduce((s, i) => s + (parseFloat(i.net_weight_kg) || 0), 0);
      const grossWt = parseFloat(document.total_gross_weight || 0) ||
        items.reduce((s, i) => s + (parseFloat(i.gross_weight_kg) || 0), 0);
      const goodsDesc = sv(document.commodity) ||
        items.map(i => sv(i.description_of_goods)).filter(Boolean).join(', ');
      const packingType = items.length > 0 ? sv(items[0].package_type, 'LOOSE') : 'LOOSE';
      const weightVal = grossWt || netWt || 0;
      const netWeightVal = netWt || grossWt || 0;

      const buyerName = sv(document.buyer_name, '').toUpperCase();
      const buyerAddr = sv(document.buyer_address, '').toUpperCase();

      let notifyTxt = sv(trBoe.notify || trBoe.to_the_order_of, '').toUpperCase();
      if (!notifyTxt) {
        const parts = [];
        if (buyerName || buyerAddr) parts.push(`${buyerName} ${buyerAddr}`.trim());
        const lcBank = sv(document.lc_opening_bank, '').toUpperCase();
        const lcBankAddr = sv(document.lc_opening_bank_address, '').toUpperCase();
        if (lcBank) parts.push(`${lcBank}${lcBankAddr ? ', ' + lcBankAddr : ''}`);
        notifyTxt = parts.join(' AND ');
      }

      const piNo = sv(document.proforma_invoice_no);
      return {
        exporter_name: sv(company.exporter_name, '').toUpperCase(),
        exporter_details: [
          sv(company.exporter_address),
          company.email ? `E MAIL:${sv(company.email)},` : '',
          company.gstin ? `GSTIN: ${sv(company.gstin)}` : ''
        ].filter(Boolean).join(' '),
        buyer_name: buyerName,
        buyer_details: buyerAddr,
        transport_mode: sv(document.mode_of_shipment, '').toUpperCase(),
        place_of_origin: sv(document.shipment_from, 'ANY PLACE OF INDIA').toUpperCase(),
        port_of_discharge: sv(document.shipment_to, '').toUpperCase(),
        final_destination: sv(document.shipment_to, '').toUpperCase(),
        gross_weight: `${weightVal.toLocaleString('en-IN')} KG`,
        packing_type: packingType.toUpperCase(),
        goods_desc: goodsDesc.toUpperCase() + ',',
        net_weight: `${netWeightVal.toLocaleString('en-IN')} KG`,
        cert_line: "CERTIFYING MERCHANDISE IS OF 'INDIA' ORIGIN.",
        usd_value: `USD ${usd(totalUsd, 2)}`,
        tin_line: `TIN ${sv(company.tin_no)} AND VAT/BIN REG.NO. ${sv(company.vat_no)} ,IRC NO. ${sv(company.irc_no)},ISSUING BANK BIN NO.${sv(document.bank_bin)} H.S. CODE : ${sv(document.hs_code)}`,
        notify: notifyTxt,
        sb_no: sv(trBoe.sb_no),
        sb_date: formatDate(trBoe.sb_date),
        lc_no: sv(document.lc_no),
        lc_date: formatDate(document.lc_opening_date),
        invoice_no: sv(document.invoice_no),
        invoice_date: formatDate(document.document_date),
        proforma_line: piNo ? `${piNo} DATED: ${formatDate(document.proforma_invoice_date)}` : '',
        iec_line: `IEC : ${sv(company.iec)}`,
        signatory: `${sv(authority?.authority_type || company?.signing_auth_type, 'Director')}.`,
      };
    },
  },

  // ── Bills of Exchange ───────────────────────────────────────────────
  billFirst: {
    title: 'First Bill of Exchange',
    icon: '💵',
    fields: BILL_FIELDS,
    buildDefaults(document, company, authority) {
      const d = buildBillOfExchangeDefaults(document, company, 'first');
      d.signatory = `${sv(authority?.authority_type, 'Director')}.`;
      return d;
    },
  },
  billSecond: {
    title: 'Second Bill of Exchange',
    icon: '💵',
    fields: BILL_FIELDS,
    buildDefaults(document, company, authority) {
      const d = buildBillOfExchangeDefaults(document, company, 'second');
      d.signatory = `${sv(authority?.authority_type, 'Director')}.`;
      return d;
    },
  },

  // ── Letter to Insurance Co. ─────────────────────────────────────────
  insurance: {
    title: 'Letter to Insurance Co.',
    icon: '🛡️',
    fields: [
      t('date_line', 'Date Line'),
      ta('to_block', 'TO (insurance company)', 2),
      ta('subject_line', 'Subject (RE) Line', 2),
      ta('intro_text', 'Intro Paragraph', 2),
      ta('buyer_block', '01. Consignee / Buyer', 2),
      ta('exporter_block', '02. Exporter / Consignor', 2),
      t('insurance_ref', '03. Insurance Ref.'),
      ta('invoice_line', '04. Invoice No., Date & Value', 2),
      t('commodity_line', '05. Commodity & Quantity'),
      t('weight_line', '06. Net & Gross Weight'),
      t('despatch_line', '07. Despatch Document No. & Date'),
      ta('carriers', '08. Carriers', 2),
      ta('other_ref', '09. Other Reff.', 2),
      t('closing_line', 'Closing Line'),
      ta('copy_buyer', 'Copy Forwarded To — Buyer', 2),
      ta('copy_bank', 'Copy Forwarded To — Bank', 2),
    ],
    buildDefaults(document, company) {
      const items = getItems(document);
      const trBoe = getTrBoe(document);
      const totalUsd = parseFloat(document.total_usd || 0);
      const netWt = netWeight(document, items);
      const grossWt = grossWeight(document, items);
      const commodity = items[0] ? sv(items[0].description_of_goods) : sv(document.commodity, 'Export Goods');
      const quantity = items[0] ? `${parseFloat(items[0].quantity || 0).toFixed(4)} ${sv(items[0].uom)}` : '';
      const exporterInfo = sv(company.exporter_address) +
        (company.email ? ` E MAIL:${sv(company.email)}, GSTIN: ${sv(company.gstin)}` : '');

      return {
        date_line: `DATE: ${formatDate(document.document_date)}`,
        to_block: sv(document.insurance_company, 'The Insurance Company\nInsurance Department'),
        subject_line: `RE : SHIPMENT(S) OF GOODS UNDER L/C NO. ${sv(document.lc_no)} DATE : ${formatDate(document.lc_opening_date)}, FOR INVOICE VALUE USD ${usd(totalUsd, 2)}`,
        intro_text: 'WE WOULD LIKE TO INFORM YOU THAT WE HAVE EFFECTED SHIPMENT OF GOODS UNDER THE ABOVE MENTIONED L/C AS DETAILS FURNISHED BELOW :-',
        buyer_block: `${sv(document.buyer_name).toUpperCase()}\n${sv(document.buyer_address)}`,
        exporter_block: `${sv(company.exporter_name).toUpperCase()}\n${exporterInfo}`,
        insurance_ref: sv(document.insurance_ref),
        invoice_line: `${sv(document.invoice_no)} DATE: ${formatDate(document.document_date)} ,\nVALUED USD ${usd(totalUsd, 2)}`,
        commodity_line: `${commodity.toUpperCase()}, QUANTITY : ${quantity},`,
        weight_line: `${netWt} KG & ${grossWt} KG`,
        despatch_line: `${sv(trBoe.despatch_doc_no)} DATE : ${formatDate(trBoe.despatch_date)}`,
        carriers: sv(trBoe.transporter_name || trBoe.carrier),
        other_ref: `H.S. CODE : ${sv(document.hs_code)}, TIN ${sv(company.tin_no)} AND VAT/BIN REG.NO. ${sv(company.vat_no)} ,IRC NO. ${sv(company.irc_no)},ISSUING BANK BIN NO.${sv(document.bank_bin)}`,
        closing_line: 'KINDLY ARRANGE INSURANCE COVERAGE ACCORDINGLY.',
        copy_buyer: `${sv(document.buyer_name).toUpperCase()} ${sv(document.buyer_address).toUpperCase()}`,
        copy_bank: `${sv(document.lc_opening_bank).toUpperCase()}, ${sv(document.lc_opening_bank_address || '').toUpperCase()}`,
      };
    },
  },

  // ── Non-Negotiable Documents ────────────────────────────────────────
  nonNegotiable: {
    title: 'Non-Negotiable Documents',
    icon: '📋',
    fields: [
      t('date_line', 'Date Line'),
      ta('re_content', 'RE : Reference Line', 3),
      ta('cert_text', 'Certification Statement', 3),
      ta('bullet1', 'Bullet 1 — Commercial Invoice', 2),
      t('bullet2', 'Bullet 2 — Packing List'),
      ta('bullet3', 'Bullet 3 — Dispatch Document', 2),
    ],
    buildDefaults(document, company) {
      const trBoe = getTrBoe(document);
      const totalUsd = parseFloat(document.total_usd) || 0;
      const carrierInfo = sv(trBoe.transporter_name || trBoe.carrier, 'TRANSPORTER DETAILS');

      return {
        date_line: `DATE: ${formatDate(document.document_date)}`,
        re_content: buildNonNegotiableRef(document, company),
        cert_text: `CERTIFIED THAT WE HAVE SENT FOLLOWING ONE SET OF NON-NEGOTIABLE DOCUMENT DIRECT TO ${sv(document.buyer_name).toUpperCase()} ${sv(document.buyer_address).toUpperCase()} BY COURIER SERVICE WITHIN 5 (FIVE) DAYS FROM THE DATE OF SHIPMENT :-`,
        bullet1: `COMMERCIAL INVOICE NO. ${sv(document.invoice_no)} DATE: ${formatDate(document.document_date)} FOR USD ${usd(totalUsd)}`,
        bullet2: 'DETAILS PACKING LIST',
        bullet3: `${sv(trBoe.despatch_doc_no)} DATE :${formatDate(trBoe.despatch_date)} OF ${carrierInfo}`,
      };
    },
  },

  // ── Form SDF ────────────────────────────────────────────────────────
  formSDF: {
    title: 'Form SDF',
    icon: '📝',
    fields: [
      t('sb_no', 'Shipping Bill No.'),
      t('sb_date', 'Shipping Bill Date'),
      t('bank_name', 'Bank Name (undertaking)'),
      t('exporter_name', 'Name of the Exporter'),
      ta('exporter_address', 'Address', 2),
    ],
    buildDefaults(document, company) {
      const trBoe = getTrBoe(document);
      let addrFull = sv(company.exporter_address, '');
      if (sv(company.email)) {
        addrFull += (addrFull ? ' E MAIL:' : 'E MAIL:') + sv(company.email) + ',';
      }
      return {
        sb_no: sv(trBoe.sb_no, ''),
        sb_date: trBoe.sb_date ? formatDate(trBoe.sb_date) : '',
        bank_name: sv(company.negotiation_bank, '').toUpperCase(),
        exporter_name: sv(company.exporter_name, '').toUpperCase(),
        exporter_address: addrFull,
      };
    },
  },

  // ── Registered Truck Operators ──────────────────────────────────────
  truckOperators: {
    title: 'Registered Truck Operators',
    icon: '🚛',
    fields: [
      t('date_line', 'Date Line'),
      ta('body_text', 'Certification Statement', 8),
    ],
    buildDefaults(document, company) {
      // This letter certifies the transport, not the courier: the goods went
      // by an operator registered with the Indian Banker's Association under
      // the exporter's confidential code, on the consignment note quoted
      // here. Every part is read from the field that holds it — the code from
      // the company's own column, the consignment note and its transporter
      // from the TR/BOE row, the credit with its amendment, the importer's
      // registration numbers and the linked proforma — and a part the invoice
      // doesn't carry is dropped instead of printed as a bare label.
      const trBoe = getTrBoe(document);
      const ibaCode = sv(company.iba_code).trim().toUpperCase();
      const despatch = buildDespatchRef(trBoe);
      const consignee = [sv(document.buyer_name), sv(document.buyer_address)]
        .filter(Boolean).join(', ').toUpperCase();
      const lcRef = buildLcRefLine(document);
      const registration = buildRegistrationRef(document, company);
      const proforma = buildProformaRef(document);
      const hsCode = sv(document.hs_code).trim();
      // The invoice number is what the date belongs to — an invoice that
      // carries neither leaves the part out instead of printing "DATE :"
      // behind a bare label.
      const invoiceNo = sv(document.invoice_no).trim();
      const invoiceRef = invoiceNo && `INVOICE NO. ${invoiceNo}`
        + (document.document_date ? ` DATE : ${formatDate(document.document_date)}` : '');
      const closing = [hsCode && `H.S. CODE ${hsCode}`, registration]
        .filter(Boolean).join(', ');

      const body = [
        'CERTIFIED THAT WE HAVE DESPATCHED THE GOODS BY TRANSPORT REGISTERED UNDER'
        + " INDIAN BANKER'S ASSOCIATION AUTHORITY"
        + (ibaCode ? ` VIDE CONFIDENTIAL CODE NUMBER ${ibaCode}` : ''),
        despatch && `THROUGH UNDER ${despatch}`,
        invoiceRef && `- ${invoiceRef}`,
        consignee && `FOR THE CONSIGNEE/ BUYER NAMED ${consignee}`,
        lcRef && `- UNDER ${lcRef}`,
        closing,
        proforma && `AND ${proforma}`,
      ].filter(Boolean).join(' ');

      return {
        date_line: `DATE: ${formatDate(document.document_date)}`,
        // Whichever part ran last ends the sentence — never on the comma the
        // L/C line carries when it stands alone.
        body_text: `${body.replace(/[\s,]+$/, '')}.`,
      };
    },
  },

  // ── Country of Origin Certificate ───────────────────────────────────
  countryOfOriginCert: {
    title: 'Country of Origin Certificate',
    icon: '🌍',
    fields: [
      t('date_line', 'Date Line'),
      ta('body_text', 'Body Paragraph', 6),
    ],
    buildDefaults(document, company) {
      const totalUsd = parseFloat(document.total_usd || 0);
      // Every part is quoted from the field that holds it: the consignment
      // note the goods travelled under (not the shipping bill, and not the
      // exporter's own address), the credit with its amendment, the
      // importer's registration numbers and the linked proforma. Whatever
      // the invoice doesn't carry is left out rather than printed as a
      // bare label.
      const despatch = buildDespatchRef(getTrBoe(document));
      const consignee = [sv(document.buyer_name), sv(document.buyer_address)]
        .filter(Boolean).join(', ').toUpperCase();
      const lcRef = buildLcRefLine(document);
      const registration = buildRegistrationRef(document, company);
      const proforma = buildProformaRef(document);
      const closing = [`H.S. CODE ${sv(document.hs_code)}`, registration]
        .filter(Boolean).join(', ');

      return {
        date_line: longToday(),
        body_text: [
          'CERTIFIED THAT COUNTRY OF ORIGIN MENTIONED EITHER PRINTED OR WRITTEN IN'
          + ' UNREMOVABLE INK ON ALL PACKAGES/ DRUM/ WOODEN BOX/ PACKET/ CARTONS/'
          + ' COVER BOX AND OTHER BOXES/ CONTAINER OF THE SUPPLIED GOODS',
          `- UNDER INVOICE NO. ${sv(document.invoice_no)} DATE: ${formatDate(document.document_date)}`
          + ` FOR USD ${usd(totalUsd)}${despatch ? `, ${despatch}` : ''}`,
          consignee && `FOR THE CONSIGNEE/ BUYER NAMED ${consignee}`,
          lcRef && `- UNDER ${lcRef}`,
          [closing, proforma && `AND ${proforma}`].filter(Boolean).join(' ') + '.',
        ].filter(Boolean).join(' '),
      };
    },
  },

  // ── L/C Compliance ──────────────────────────────────────────────────
  lcCompliance: {
    title: 'Terms & Conditions of L/C Complied With',
    icon: '✅',
    fields: [
      t('date_line', 'Date Line'),
      ta('cert_statement', 'Certification Statement', 7),
    ],
    buildDefaults(document, company) {
      const trBoe = getTrBoe(document);
      const totalUsd = parseFloat(document.total_usd || 0);
      return {
        date_line: longToday(),
        cert_statement: `CERTIFIED THAT WHILE DESPATCHING THE GOODS TO THE CONSIGNEE/ BUYER ${sv(document.buyer_name).toUpperCase()}, ${sv(document.buyer_address).toUpperCase()}, UNDER ${sv(trBoe.despatch_doc_no)} DATE : ${formatDate(trBoe.despatch_date)} OF ${sv(trBoe.transporter_name || trBoe.carrier).toUpperCase()} REGARDING INVOICE NO. ${sv(document.invoice_no)} DATE: ${formatDate(document.document_date)} FOR USD ${usd(totalUsd)} - UNDER L/C NO. ${sv(document.lc_no)} DATE : ${formatDate(document.lc_opening_date)}, ISSUED BY ${sv(document.lc_opening_bank).toUpperCase()}, ${sv(document.lc_opening_bank_address || '').toUpperCase()} H.S. CODE ${sv(document.hs_code)}, TIN ${sv(company.tin_no)} AND VAT/BIN REG.NO. ${sv(company.vat_no)} ,IRC NO. ${sv(company.irc_no)},ISSUING BANK BIN NO.${sv(document.bank_bin)} WE HAVE BEEN COMPLIED ALL TERMS AND CONDITIONS OF THIS L/C AND ${sv(document.proforma_invoice_no)} DATED: ${formatDate(document.proforma_invoice_date)}.`,
      };
    },
  },

  // ── Importer Name, Address & TIN Printed ────────────────────────────
  importerNameAddress: {
    title: 'Importer Name, Address and TIN No. Printed',
    icon: '🏷️',
    fields: [
      t('date_line', 'Date Line'),
      ta('cert_statement', 'Certification Statement', 8),
    ],
    buildDefaults(document, company) {
      const trBoe = getTrBoe(document);
      const totalUsd = parseFloat(document.total_usd || 0);
      // The importer's own registration numbers — the subject of this
      // certificate, and quoted again beside the H.S. code. They live in the
      // invoice's registration line, not in the exporter's company columns.
      const registration = buildRegistrationRef(document, company);
      const lcRef = buildLcRefLine(document);
      const proforma = buildProformaRef(document);
      const closing = [`H.S. CODE ${sv(document.hs_code)}`, registration]
        .filter(Boolean).join(', ');
      const complied = ['WE HAVE BEEN COMPLIED ALL TERMS AND CONDITIONS OF THIS L/C',
        proforma && `AND ${proforma}`].filter(Boolean).join(' ');

      return {
        date_line: longToday(),
        cert_statement: [
          `CERTIFIED THAT IMPORTER'S NAME, ADDRESS AND ${registration || 'TIN'} PRINTED OR WRITTEN IN UN-REMOVABLE INK ON ALL PACKAGE/ DRUM/ WOODEN BOX/ COVER BOX/ CARTON AND OTHER BOXES OF THE SUPPLIED GOODS`,
          `WHILE DESPATCHING THE GOODS TO THE CONSIGNEE/ BUYER ${sv(document.buyer_name).toUpperCase()}, ${sv(document.buyer_address).toUpperCase()}, THROUGH ${sv(trBoe.despatch_doc_no)} DATE : ${formatDate(trBoe.despatch_date)} OF ${sv(trBoe.transporter_name || trBoe.carrier).toUpperCase()}`,
          `VIDE INVOICE NOS. ${sv(document.invoice_no)} DATE: ${formatDate(document.document_date)} FOR USD ${usd(totalUsd)}`,
          lcRef && `- UNDER ${lcRef}`,
          `${closing}. ${complied}.`,
        ].filter(Boolean).join(' '),
      };
    },
  },

  // ── Pre-Shipment Inspection ─────────────────────────────────────────
  preShipmentInspection: {
    title: 'Pre-Shipment Inspection by Beneficiary',
    icon: '🔍',
    fields: [
      t('date_line', 'Date Line'),
      ta('cert_statement', 'Certification Statement', 8),
    ],
    buildDefaults(document, company) {
      const totalUsd = parseFloat(document.total_usd || 0);
      return {
        date_line: `DATE: ${formatDate(document.document_date)}`,
        cert_statement: `CERTIFIED THAT WE HAVE CONDUCTED PRE-SHIPMENT INSPECTION OF THE CONSIGNMENT BOOKED BY US UNDER L/C NO. ${sv(document.lc_no)} DATE: ${formatDate(document.lc_opening_date)} ISSUED BY ${sv(document.lc_opening_bank).toUpperCase()}, ${sv(document.lc_opening_bank_address || '').toUpperCase()} AND INVOICE NO. ${sv(document.invoice_no)} DATE : ${formatDate(document.document_date)} FOR USD ${usd(totalUsd)} WHILE DESPATCHING THE GOODS TO ${sv(document.buyer_name).toUpperCase()}, ${sv(document.buyer_address).toUpperCase()}, WE HEREBY CONFIRMED THAT MERCHANDISE HAVE BEEN SHIPPED STRICTLY AS PER SPECIFICATIONS/ QUALITY/ QUANTITY AND OTHER TERMS AS STIPULATED IN THE ${sv(document.proforma_invoice_no)} DATED: ${formatDate(document.proforma_invoice_date)} OF THE BENEFICIARY. TIN ${sv(company.tin_no)} AND VAT/BIN REG.NO. ${sv(company.vat_no)} ,IRC NO. ${sv(company.irc_no)},ISSUING BANK BIN NO.${sv(document.bank_bin)} AND H.S. CODE : ${sv(document.hs_code)}`,
      };
    },
  },

  // ── Weight & Measurement List ───────────────────────────────────────
  weightMeasurement: {
    title: 'Weight and Measurement List',
    icon: '⚖️',
    fields: [
      t('date_line', 'Date Line'),
      t('invoice_line', '01. Invoice No. and Date'),
      t('buyer_contract', '02. Buyer Contract No. and Date'),
      ta('lc_line', '03. L/C No. and Date', 2),
      t('commodity_line', '04. Commodity and Quantity'),
      t('net_weight_line', '05a. Net Weight'),
      t('gross_weight_line', '05b. Gross Weight'),
      t('packages_line', '06. Total Packages'),
      ta('buyer_block', '07. Consignee / Buyer / Applicant', 2),
      ta('other_ref', '08. Other Ref.', 2),
      ta('cert_statement', 'Certification Statement', 2),
    ],
    buildDefaults(document, company) {
      const items = getItems(document);
      const netWt = netWeight(document, items);
      const grossWt = grossWeight(document, items);
      const commodity = items[0] ? sv(items[0].description_of_goods) : sv(document.commodity, 'Export Goods');
      const quantity = items[0] ? `${parseFloat(items[0].quantity || 0).toFixed(4)} ${sv(items[0].uom).toUpperCase()}` : '';
      // Every line item's packages added up against the one fixed unit: "8 PKGS"
      const packagesLine = buildPackagesLine(document, items) || 'LOOSE';

      return {
        date_line: longToday(),
        invoice_line: `${sv(document.invoice_no)} AND DATE: ${formatDate(document.document_date)}`,
        buyer_contract: `${sv(document.proforma_invoice_no)} DATED: ${formatDate(document.proforma_invoice_date)}`,
        lc_line: `L/C NO. ${sv(document.lc_no)} DATE: ${formatDate(document.lc_opening_date)} ISSUED BY ${sv(document.lc_opening_bank).toUpperCase()}, ${sv(document.lc_opening_bank_address || '').toUpperCase()}`,
        commodity_line: `${commodity.toUpperCase()}, QUANTITY : ${quantity},`,
        net_weight_line: `NET WEIGHT : ${Math.round(parseFloat(netWt))} KG AND`,
        gross_weight_line: `GROSS WEIGHT : ${Math.round(parseFloat(grossWt))} KG`,
        packages_line: packagesLine,
        buyer_block: `${sv(document.buyer_name).toUpperCase()} ${sv(document.buyer_address).toUpperCase()}`,
        other_ref: buildHsRegistrationLine(document, company),
        cert_statement: `CERTIFIED THAT THE WEIGHT AND MEASURMENT LIST REQUIRED AS PER L/C NO. ${sv(document.lc_no)} DATE: ${formatDate(document.lc_opening_date)}    AND AS PER ${sv(document.proforma_invoice_no)} DATED: ${formatDate(document.proforma_invoice_date)}.`,
      };
    },
  },

  // ── Fax Transmission ────────────────────────────────────────────────
  faxTransmission: {
    title: 'Fax Transmission',
    icon: '📠',
    fields: SHIPMENT_NOTICE_FIELDS,
    buildDefaults(document, company) {
      const trBoe = getTrBoe(document);
      const items = getItems(document);
      const totalUsd = parseFloat(document.total_usd || 0);
      const amountWords = amountInWords(document, totalUsd);
      const netWt = netWeight(document, items);
      const grossWt = grossWeight(document, items);
      const commodity = items[0] ? sv(items[0].description_of_goods) : sv(document.commodity, 'Export Goods');
      const quantity = items[0] ? `${parseFloat(items[0].quantity || 0).toFixed(4)} ${sv(items[0].uom).toUpperCase()}` : '';
      // Every line item's packages added up against the one fixed unit: "8 PKGS"
      const packagesLine = buildPackagesLine(document, items) || 'LOOSE';
      const lcOpeningBank = sv(document.lc_opening_bank).toUpperCase();
      const lcOpeningBankAddr = sv(document.lc_opening_bank_address, '').toUpperCase();

      return {
        date_line: `DATE: ${formatDate(document.document_date)}`,
        to_block: `${lcOpeningBank},\n${lcOpeningBankAddr}`,
        ref_text: `REF. NO.: L/C NO. ${sv(document.lc_no)} DATE ${formatDate(document.lc_opening_date)}  ISSUED BY ${lcOpeningBank}, ${lcOpeningBankAddr}`,
        body_text: `KINDLY NOTE THAT WE HAVE EFFECTED THE FOLLOWING SHIPMENT FROM ${sv(document.shipment_from, 'ANY PLACE OF INDIA').toUpperCase()} TO ${sv(document.shipment_to, 'BENAPOLE').toUpperCase()}, BANGLADESH BY TRUCK /BY ROAD AS PER THE FOLLOWING DETAILS :-`,
        carrier_line: `${formatDate(trBoe.despatch_date || document.document_date)} (AS PER ${sv(trBoe.despatch_doc_no)} DATE: ${formatDate(trBoe.despatch_date)} OF ${sv(trBoe.transporter_name || trBoe.carrier).toUpperCase()}`,
        buyer_block: `${sv(document.buyer_name).toUpperCase()} ${sv(document.buyer_address).toUpperCase()}`,
        invoice_line: `${sv(document.invoice_no)} DATE: ${formatDate(document.document_date)}`,
        amount_line: `USD. ${usd(totalUsd)} (${amountWords.toUpperCase()})`,
        commodity_line: `${commodity.toUpperCase()}, QUANTITY : ${quantity},`,
        net_weight_line: `NET WEIGHT : ${parseFloat(netWt)} KG AND`,
        gross_weight_line: `GROSS WEIGHT : ${parseFloat(grossWt)} KG`,
        packages_line: packagesLine,
        other_ref: buildHsRegistrationLine(document, company),
      };
    },
  },

  // ── Shipment Advice to the Opening Bank ─────────────────────────────
  shipmentAdvice: {
    title: 'Shipment Advice to the Opening Bank',
    icon: '📨',
    fields: SHIPMENT_NOTICE_FIELDS,
    buildDefaults: buildShipmentAdviceBankDefaults,
  },

  // ── Shipment Advice to the Insurance Co. ────────────────────────────
  shipmentAdviceInsurance: {
    title: 'Shipment Advice to the Insurance Co.',
    icon: '🛡️',
    fields: SHIPMENT_ADVICE_FIELDS,
    buildDefaults(document, company) {
      const common = buildShipmentAdviceCommon(document, company);
      return {
        ...common,
        to_block: sv(document.insurance_company).toUpperCase(),
      };
    },
  },

  // ── Shipment Advice to the Buyer ────────────────────────────────────
  shipmentAdviceBuyer: {
    title: 'Shipment Advice to the Buyer',
    icon: '📨',
    fields: [
      t('date_line', 'Date Line'),
      ta('to_block', 'TO (buyer block)', 2),
      ta('ref_line1', 'Ref Line 1 (L/C)', 5),
      ta('ref_line2', 'Ref Line 2', 2),
      ta('ref_line3', 'Ref Line 3 (cover note)', 2),
      ta('body_text', 'Body Paragraph', 2),
      ta('buyer_block', '01. Consignee / Buyer / Applicant', 2),
      t('invoice_line', '02. Invoice No. & Date'),
      t('invoice_value', '03. Invoice Value'),
      ta('goods_line', '04. Description of Goods', 2),
      t('weight_line', '05. Net & Gross Weight'),
      t('despatch_line', '06. Despatch Document No. & Date'),
      ta('carrier', '07. Carrier', 2),
      t('closing_line', 'Closing Statement'),
    ],
    buildDefaults(document, company) {
      const common = buildShipmentAdviceCommon(document, company);
      return {
        ...common,
        to_block: `${sv(document.buyer_name).toUpperCase()},\n${sv(document.buyer_address).toUpperCase()}`,
      };
    },
  },

  // ── Non-Israeli Flag Vessel Certificate ─────────────────────────────
  nonIsraeliFlagVessel: {
    title: 'Non-Israeli Flag Vessel Cert. from the Carrier',
    icon: '🚢',
    fields: [
      t('carrier_header', 'Carrier Letterhead'),
      t('date_line', 'Date Line'),
      ta('body_text', 'Body Paragraph', 8),
      t('signed_for', 'Signed For (FOR, ...)'),
    ],
    buildDefaults(document, company) {
      // The carrier's own certificate, and every part of its one sentence is
      // quoted from the field that actually holds it: the consignment note
      // and the date it moved on from the TR/BOE row, the credit with its
      // amendment from the invoice, the linked proforma from the proforma
      // association, and the importer's registration numbers from the single
      // line that carries them inside Declaration / Other Reference — the
      // exporter's own company columns are only a fallback. A part the
      // invoice has no data for is dropped rather than printed as a bare
      // "IRC NO." or a dangling "PROFORMA INVOICE NO. DATED:".
      const trBoe = getTrBoe(document);

      // Consignor: the name and address run on as one block, followed by the
      // contact details that block does not already carry.
      const consignorContact = [
        sv(company.email).trim() && `E MAIL:${sv(company.email).trim().toLowerCase()}`,
        sv(company.gstin).trim() && `GSTIN: ${sv(company.gstin).trim()}`,
      ].filter(Boolean).join(', ');
      const consignor = [
        [sv(company.exporter_name).trim(), sv(company.exporter_address).trim()]
          .filter(Boolean).join(' ').toUpperCase(),
        consignorContact,
      ].filter(Boolean).join(' ');

      // The buyer's name is usually typed ending in "LTD." already, so the
      // address follows it directly instead of behind a second full stop.
      const buyer = [sv(document.buyer_name).trim(), sv(document.buyer_address).trim()]
        .filter(Boolean).join(' ').toUpperCase();

      const despatchNo = sv(trBoe.despatch_doc_no).trim();
      const despatchDate = formatDate(trBoe.despatch_date || document.document_date);
      const invoiceNo = sv(document.invoice_no).trim();
      // The credit runs on inside the sentence — it names its own bank and
      // keeps none of the trailing comma it carries when it stands alone.
      const credit = buildLcRefLine(document, { dateLabel: 'DATE:', separator: ' ' })
        .replace(/,\s*$/, '');
      const proforma = buildProformaRef(document);
      const registration = buildRegistrationRef(document, company);
      const hsCode = sv(document.hs_code).trim();

      const refs = [proforma && `REGARDING ${proforma}`, registration]
        .filter(Boolean).join(', ');
      const tail = [
        credit && `UNDER ${credit}`,
        [refs, hsCode && `H.S. CODE NO.${hsCode}`].filter(Boolean).join(' AND '),
      ].filter(Boolean).join(', ');

      const body = [
        'THIS IS TO CERTIFY THAT THE CONSIGNMENT BOOKED',
        sv(document.shipment_from).trim() &&
          `FROM ${sv(document.shipment_from).trim().toUpperCase()}`,
        consignor && `BY ${consignor}`,
        sv(document.shipment_to).trim() &&
          `DESTAINED TO ${sv(document.shipment_to).trim().toUpperCase()}`,
        buyer && `THROUGH FOR ${buyer}`,
        despatchNo && `VIDE OUR ${despatchNo}${despatchDate ? ` DATED:${despatchDate}` : ''}`,
        CARRIER_CERTIFICATION,
        invoiceNo && `AGAINST EXPORT INVOICE NO. ${invoiceNo}`
          + (document.document_date ? ` DATE:${formatDate(document.document_date)}` : ''),
        tail,
      ].filter(Boolean).join(' ');

      return {
        carrier_header: sv(trBoe.transporter_name || trBoe.carrier, 'THE CARRIER').toUpperCase(),
        date_line: `DATE: ${formatDate(document.document_date)}`,
        // Names and addresses are typed with stray double spaces in them; the
        // certificate prints as one justified paragraph, so they are closed up
        // to single spaces before the text is wrapped.
        body_text: body.replace(/\s+/g, ' ').trim(),
        signed_for: 'THE CARRIER',
      };
    },
  },

  // ── Beneficiary Certificate ─────────────────────────────────────────
  beneficiaryCertificate: {
    title: 'Beneficiary Certificate',
    icon: '📋',
    fields: [
      t('date_line', 'Date Line'),
      fx('ref_text', 'Reference Paragraph — from the invoice', 5),
      tw('para1', 'Paragraph 1 — Truck Operator', 4),
      tw('para2', 'Paragraph 2 — Country of Origin', 3),
      tw('para3', 'Paragraph 3 — Shipped in Conformity', 3),
      tw('para4', 'Paragraph 4 — As per Proforma', 3),
    ],
    buildDefaults(document, company) {
      const trBoe = getTrBoe(document);
      const carrierFull = sv(trBoe.transporter_name || trBoe.carrier).toUpperCase();
      const confidentialCode = sv(trBoe.iba_code || 'CAJ-1233');

      // Every part of the reference is quoted from the field that holds it:
      // the credit with its amendment, the importer's registration numbers,
      // the linked proforma and the consignment note the goods travelled
      // under. Whatever the invoice doesn't carry is left out rather than
      // printed as a bare "TIN" or a dangling "DATED:".
      //
      // The sentence names the credit itself ("ISSUED UNDER ... OF <bank>")
      // and punctuates its own list, so the line arrives unpunctuated.
      const credit = buildLcRefLine(document, { dateLabel: 'DATE', bankLabel: 'OF', separator: ' ' })
        .replace(/,$/, '');
      const registration = buildRegistrationRef(document, company);
      const proforma = buildProformaRef(document);
      const despatch = buildDespatchRef(trBoe);
      const hsCode = sv(document.hs_code, '').trim();

      return {
        date_line: longToday(),
        ref_text: [
          `THIS CERTIFICATE IS RELATED TO OUR INVOICE NO. ${sv(document.invoice_no)} DATE: ${formatDate(document.document_date)}`,
          [
            credit && `ISSUED UNDER ${credit}`,
            registration,
            hsCode && `H.S. CODE NO. ${hsCode}`,
            proforma,
          ].filter(Boolean).join(', '),
          despatch && `DESPATCHED THROUGH ${despatch}`,
        ].filter(Boolean).join(' '),
        para1: `WE HEREBY CERTIFY THAT THE ${sv(trBoe.despatch_doc_no)} DATED: ${formatDate(trBoe.despatch_date)} HAS BEEN ISSUED BY A RECOGNISED TRUCK OPERATOR NAMED ${carrierFull}. WHO ARE APPROVED BY INDIAN BANK'S ASSOCIATION & THEIR CONFIDENTIAL CODE NUMBER IS \`${confidentialCode}\` AS ALLOTED BY INDIAN BANK'S ASSOCIATION.`,
        para2: '  WE HEREBY CERTIFY THAT THE COUNTRY OF ORIGIN INDIA HAVE BEEN MENTIONED ON ALL MERCHANDISE PACKING',
        // With no proforma linked, both paragraphs stand on the credit alone
        // rather than certifying goods "AS PER OUR  DATED: ".
        para3: '  WE HEREBY FURTHER CERTIFY THAT THE GOODS HAVE BEEN SHIPPED IN CONFORMITY WITH'
          + ` THE TERMS AND CONDITIONS OF THE L/C${proforma ? ` AND ALSO AS PER ${proforma}` : ''}`,
        para4: proforma
          ? `  WE ALSO HEREBY CERTIFY THAT THE GOODS ARE STRICTLY AS PER OUR ${proforma} AND ALL TERMS AND CONDITIONS THEREOF HAVE BEEN COMPLIED WITH.`
          : '  WE ALSO HEREBY CERTIFY THAT ALL TERMS AND CONDITIONS OF THE L/C HAVE BEEN COMPLIED WITH.',
      };
    },
  },

  // ── Beneficiary Certificate Content Type II ─────────────────────────
  beneficiaryCertificateTypeII: {
    title: 'Beneficiary Certificate Content Type II',
    icon: '📑',
    fields: [
      t('date_line', 'Date Line'),
      ta('ref_line1', 'Ref Line 1 — L/C', 2),
      t('ref_line2', 'Ref Line 2 — Invoice'),
      ta('ref_line3', 'Ref Line 3 — Consignee / Buyer', 2),
      ta('ref_line4', 'Ref Line 4 — Proforma / TIN / VAT', 2),
      ta('para1', 'Paragraph 1 — Truck Operator', 3),
      ta('para2', 'Paragraph 2 — Country of Origin', 2),
      ta('para3', 'Paragraph 3 — Shipped in Conformity', 2),
      ta('para4', 'Paragraph 4 — As per Proforma', 2),
      ta('para5', 'Paragraph 5 — Non-Negotiable Docs Sent', 3),
      ta('para6', 'Paragraph 6 — Pre-Inspection', 2),
    ],
    buildDefaults(document, company) {
      const trBoe = getTrBoe(document);
      const carrierFull = sv(trBoe.transporter_name || trBoe.carrier).toUpperCase();
      const confidentialCode = sv(trBoe.iba_code || 'CAJ-1233');
      return {
        date_line: longToday(),
        ref_line1: `L/C NO. ${sv(document.lc_no)} DATE : ${formatDate(document.lc_opening_date)}, ISSUED BY ${sv(document.lc_opening_bank).toUpperCase()}, ${sv(document.lc_opening_bank_address, '').toUpperCase()}`,
        ref_line2: `INVOICE NO. ${sv(document.invoice_no)} DATE: ${formatDate(document.document_date)}`,
        ref_line3: `CONSIGNEE/ BUYER/APPLICANT : ${sv(document.buyer_name).toUpperCase()}, ${sv(document.buyer_address).toUpperCase()}`,
        ref_line4: `${sv(document.proforma_invoice_no)}  DATED:  ${formatDate(document.proforma_invoice_date)}  TIN ${sv(company.tin_no)}  AND  VAT/BIN  REG.NO. ${sv(company.vat_no)} ,IRC NO. ${sv(company.irc_no)},ISSUING BANK BIN NO.${sv(document.bank_bin)} H.S. CODE NO. ${sv(document.hs_code)}`,
        para1: `WE HEREBY CERTIFY THAT THE ${sv(trBoe.despatch_doc_no)} DATED: ${formatDate(trBoe.despatch_date)} HAS BEEN ISSUED BY A RECOGNISED TRUCK OPERATOR NAMED ${carrierFull}. WHO ARE APPROVED BY INDIAN BANK'S ASSOCIATION & THEIR CONFIDENTIAL CODE NUMBER IS \`${confidentialCode}\` AS ALLOTED BY INDIAN BANK'S ASSOCIATION.`,
        para2: 'WE HEREBY CERTIFY THAT THE COUNTRY OF ORIGIN INDIA HAVE BEEN MENTIONED ON ALL MERCHANDISE PACKING',
        para3: `WE HEREBY FURTHER CERTIFY THAT THE GOODS HAVE BEEN SHIPPED IN CONFORMITY WITH THE TERMS AND CONDITIONS OF THE L/C AND ALSO AS PER ${sv(document.proforma_invoice_no)} DATED: ${formatDate(document.proforma_invoice_date)}`,
        para4: `WE ALSO HEREBY CERTIFY THAT THE GOODS ARE STRICTLY AS PER OUR ${sv(document.proforma_invoice_no)} DATED: ${formatDate(document.proforma_invoice_date)} AND ALL TERMS AND CONDITIONS THEREOF HAVE BEEN COMPLIED WITH.`,
        para5: 'WE HEREBY CERTIFY THAT ONE SET OF NON-NEGOTIABLE SHIPPING DOCUMENTS PERTAINING TO THE SHIPMENT MADE UNDER ABOVE REF HAS BEEN SENT DIRECLTY TO THE APPLICANT/YOU IMMEDIATELY AFTER SHIPMENT BY REGISTERED AIRMAIL/DHL COURIER SERVICE. THE COPY OF RELEVANT COURIER RECEIPT IS ENCLOSED HEREWITH.',
        para6: 'AS A BENEFICIARY WE HAVE PRE-INSPECTED THE GOODS PERTAINING TO THE SHIPMENT MADE UNDER ABOVE REF.',
      };
    },
  },

  // ── ABC International certificates ──────────────────────────────────
  phytosanitaryCertificate: {
    title: 'Phytosanitary Certificate by ABC International',
    icon: '🌿',
    fields: ABC_CERT_FIELDS,
    buildDefaults(document, company) {
      return buildAbcCertificateDefaults(document, company,
        `THE PLANTS AND PLANT MATERIALS ARE FREE FROM INJURIOUS INSECT PEST AND DISEASE OF RENDERED SO BY EFFECTIVE TREATMENT. IRC NO.${sv(company.irc_no)}, TIN NO.${sv(company.tin_no)}, VAT/BIN REG. NO.${sv(company.vat_no)} AND LC ISSUING BANK'S BIN NO.${sv(document.bank_bin)}`);
    },
  },
  radioactivityTestCertificate: {
    title: 'Radioactivity Test Certificate by ABC International',
    icon: '☢️',
    fields: ABC_CERT_FIELDS,
    buildDefaults(document, company) {
      return buildAbcCertificateDefaults(document, company,
        `THE SAMPLE WAS ANALYSED FOR IT'S "RADIOACTIVITY" CONTENT AND THIS IS TO CERTIFYING THAT THE LEVEL OF CS-137 (ACTIVITY) IN THE SAMPLE, THE RADIATION LEVEL IS NOT ABOVE 50 BQ PER KG. THE SAMPLE IS ABSOLUTELY FREE FROM MAN-MADE RADIOACTIVE CONTAMINATION, AS SUCH THE SAMPLE IS "FIT FOR HUMAN/ POULTRY/ ANIMAL CONSUMPTION" FROM THE RADIOLOGICAL POINT OF VIEW.\nIRC NO.${sv(company.irc_no)}, TIN NO.${sv(company.tin_no)}, VAT/BIN REG. NO.${sv(company.vat_no)} AND LC ISSUING BANK'S BIN NO.${sv(document.bank_bin)}`);
    },
  },
  healthCertificate: {
    title: 'Health Certificate by ABC International',
    icon: '🏥',
    fields: ABC_CERT_FIELDS,
    buildDefaults(document, company) {
      return buildAbcCertificateDefaults(document, company,
        `THE SAMPLE WAS ANALYSED SCIENTIFICALLY AND CERTIFYING THAT THE GOODS SHIPPED IS NOT HARMFUL FOR HUMAN CONSUMPTION.\nIRC NO.${sv(company.irc_no)}, TIN NO.${sv(company.tin_no)}, VAT/BIN REG. NO.${sv(company.vat_no)} AND LC ISSUING BANK'S BIN NO.${sv(document.bank_bin)}`);
    },
  },

  // ── Letter to Bank for Negotiation (Front) ──────────────────────────
  letterToBank: {
    title: 'Letter to Bank for Negotiation (Front)',
    icon: '🏦',
    fields: [
      ta('drawer_block', 'Drawer (Exporter)', 3),
      t('invoice_ref_line1', 'Invoice No. Line'),
      t('invoice_ref_line2', 'Invoice Date Line'),
      t('iec_value', 'IEC'),
      t('buyer_order', "Buyer's Order No. & Dt."),
      ta('consignee_block', 'Drawee (Consignee)', 2),
      ta('drawee_bank', 'Drawee Bank', 2),
      t('country_of_origin', 'Country of Origin'),
      t('mode_of_shipment', 'Mode of Shipment'),
      t('shipment_from', 'Shipment From / Port of Loading'),
      t('shipment_to', 'Shipment To / Port of Discharge'),
      ta('row_invoice', 'Row i — Invoice', 2),
      ta('row_packing', 'Row ii — Packing List', 2),
      ta('row_tr', 'Row iii — TR / BL / AWB', 2),
      ta('row_sb', 'Row iv — Bill of Export / S.B.', 2),
      ta('row_boe', 'Row ix — Bill of Exchange / Draft', 2),
      ta('row_lc', 'Row x — L/C', 2),
      ta('instr_lc', 'Instruction viii — Negotiable Documents', 2),
      ta('instr_credit', 'Instruction ix — Credit Our A/C', 2),
      t('signatory', 'Signatory'),
    ],
    buildDefaults(document, company) {
      const trBoe = getTrBoe(document);
      const totalUsd = parseFloat(document.total_usd || 0);
      const lcValueUsd = parseFloat(document.lc_value_usd || 0);
      const buyOrd = document.proforma || {};
      const lcNo = sv(document.lc_no);
      const lcDt = formatDate(document.lc_opening_date);
      const lcBank = sv(document.lc_opening_bank).toUpperCase();
      const lcBankAddr = sv(document.lc_opening_bank_address, '').toUpperCase();
      // Name + branch + address all live in negotiation_bank — never append.
      const negBank = sv(company.negotiation_bank, 'UNION BANK OF INDIA, OVERSEAS BRANCH').toUpperCase();
      const modeTxt = sv(document.mode_of_shipment, 'ROAD/BY TRUCK').toUpperCase();

      return {
        drawer_block: `${sv(company.exporter_name).toUpperCase()}\n${sv(company.exporter_address).toUpperCase()}. E MAIL:${sv(company.email)}, GSTIN:\n${sv(company.gstin)}`,
        invoice_ref_line1: `${sv(document.invoice_no)} DATED`,
        invoice_ref_line2: formatDate(document.document_date),
        iec_value: sv(company.iec),
        buyer_order: `${sv(buyOrd.invoice_no)} DATED: ${formatDate(buyOrd.document_date)}`,
        consignee_block: `${sv(document.buyer_name).toUpperCase()}\n${sv(document.buyer_address).toUpperCase()}`,
        drawee_bank: `${lcBank}, ${lcBankAddr}`,
        country_of_origin: sv(document.country_of_origin, 'INDIA').toUpperCase(),
        mode_of_shipment: modeTxt.startsWith('BY ') ? modeTxt : `BY ${modeTxt}`,
        shipment_from: sv(document.shipment_from).toUpperCase(),
        shipment_to: sv(document.shipment_to).toUpperCase(),
        row_invoice: `i. Invoice No. ${sv(document.invoice_no)} Date :\n${formatDate(document.document_date)}`,
        row_packing: `ii. Packing List No. ${sv(document.invoice_no)} Date :\n${formatDate(document.document_date)}`,
        row_tr: `iii. TR / BL / AWB No. ${sv(trBoe.despatch_doc_no)} Date :\n${formatDate(trBoe.despatch_date)}`,
        row_sb: `iv. Bill of Export/ S.B. No. ${sv(trBoe.sb_no)} Date :\n${formatDate(trBoe.sb_date)}`,
        row_boe: `ix. Bill of Exchange/ Draft for Amount USD:\n${usd(totalUsd)} Tenor : LC AT SIGHT`,
        row_lc: `x. L/C No. ${lcNo} Date :\n${lcDt}, Amount : USD ${lcValueUsd.toLocaleString('en-IN')},`,
        instr_lc: `viii. Negotiable documents drawn under L/C No.\n${lcNo} Date : ${lcDt} Issued by ${lcBank}, ${lcBankAddr}`,
        instr_credit: `ix. Credit our Current A/C No. ${sv(company.bank_account_no)}\nwith ${negBank} on realisation of payment`,
        signatory: sv(company.signing_auth_type, 'Director'),
      };
    },
  },

  // ── Letter to Bank for Negotiation (Back) — Terms & Conditions ──────
  termsConditions: {
    title: 'Letter to Bank for Negotiation (Back)',
    icon: '🏦',
    fields: [
      ta('term1', 'Term 1', 4),
      ta('term2', 'Term 2', 2),
      ta('term3', 'Term 3', 5),
      ta('term4', 'Term 4', 3),
      ta('term5', 'Term 5', 5),
      ta('term6', 'Term 6', 5),
      t('term7_intro', 'Term 7 — Intro'),
      ta('term7a', 'Term 7a', 2),
      ta('term7b', 'Term 7b', 2),
      ta('term7c', 'Term 7c', 2),
      ta('term8', 'Term 8', 4),
      t('signatory', 'Signatory'),
    ],
    buildDefaults(document, company, authority) {
      return {
        term1: `We agree to accept the rate of exchange ruling on the day that the documents are approved by your authorized office and undertake to agree to any consequent adjustment in the rupee equivalent if the proceeds have been credited / paid to us. However, In case of the bills being covered by a specified forward contract, the above shall not apply and the terms of the forward contract shall be binding on us.`,
        term2: `In case of any discrepancies noticed by the Bank, please treat the documents on collection basis/ return the documents to us.`,
        term3: `Negotiations are undertaken on the understanding that the Bank retains recourse to its customers, not only for the due payment of the bills at the original maturity date as stated in the bills but also for any loss which may be incurred owing to a moratorium or currency restrictions imposed, or to be imposed, in the country of payment. Moreover, negotiations are undertaken only on the terms that the Bank is not liable for loss, damage, or delay however cause, which is not directly due to the negligence or default of its officers or servants.`,
        term4: `If the bill which has been purchased / discounted remains unpaid/ unaccepted, the due on the bill together with interest and other charges may be debited to my / our account or otherwise recovered from me/ us.`,
        term5: `We authorize the Bank to take delivery and also to do all other acts necessary for the safety, preservation and storage of the merchandise relative to the accompanying documents and to sell without further notice to us at our risk, cost and expenses part or whole of the said merchandise accompanying this bill, or otherwise coming into your possession towards the realization of any dues,costs, or expenses incurred by the Bank or otherwise outstanding against us at any time on this or any other account, and that the account of sale and of your expenses shall be accepted by us as correct and conclusive.`,
        term6: `We assure that the payment of the bill will be realized on first presentation in case of sight bill/ on due date in case of usance bill. In case bill is not paid on due date we are aware that, as per RBI FEDAI rule / guidelines the bill will be crystallized on the 30th day of the due date/ national due date. We hereby irrevocable authorize you to crystallize the bill on such 30th Day of the due date/ notional due date. We also hereby agree to pay all charges including exchange differences. We irrevocable authorize you to recover these charges from our account.`,
        term7_intro: 'We agree that the Bank shall not be responsible for :',
        term7a: `Any act, omission, default, suspension, insolvency or bankruptcy of any correspondent, agent or sub-agent , or`,
        term7b: `For any delay in remittance or loss in exchange during transmission or in the course of collection, or`,
        term7c: `For loss of any bill of exchange or documents in transit or in the possession of any correspondent, agent or sub-agent.`,
        term8: `Unless otherwise specified, this collection is subject to the Uniform Rules for the Collection of Commercial Paper, International Chamber of Commerce Brochure No. 322. (Unless otherwise specified, I/ We also undertake and agree to pay to the Bank and hereby authorize the Bank to reimburse itself for all commission and collection charges and all expenses incurred in the handling of this item if such charges and expenses are not paid by the drawee).`,
        signatory: sv(company.signing_auth_type, sv(authority?.designation, 'Authorized Signatory')),
      };
    },
  },
};

// ── Saved-data access + resolution ──────────────────────────────────────

// Returns the saved (user-edited) field map for a document + PDF type, or
// null when nothing has been saved yet.
export function getSavedPdfData(document, docType) {
  const list = document?.pdfDataList || document?.pdf_data_list || [];
  const record = list.find((r) => r.doc_type === docType);
  if (!record || !record.data) return null;
  try {
    const parsed = typeof record.data === 'string' ? JSON.parse(record.data) : record.data;
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch (_) {
    return null;
  }
}

// The fields a schema prints straight from the invoice — never editable, and
// never taken from saved data even if an older save carries a copy.
export function fixedFieldKeys(docType) {
  const schema = PDF_EDIT_SCHEMAS[docType];
  if (!schema) return [];
  return schema.fields.filter((f) => f.fixed).map((f) => f.key);
}

// The fields the exporter owns outright — clearing one prints nothing rather
// than dropping back to the wording the invoice suggested.
export function ownFieldKeys(docType) {
  const schema = PDF_EDIT_SCHEMAS[docType];
  if (!schema) return [];
  return schema.fields.filter((f) => f.own).map((f) => f.key);
}

// Merges saved edits over invoice-derived defaults. Saved values win
// field-by-field; empty/missing saved fields keep following the invoice
// (same rule the truck receipt uses).
// A save made against the letter a document used to be is not an edit of the
// letter it is now: the opening bank's advice was typed on the insurer's
// certificate rows until it was given the bank's own, so a blob still carrying
// those rows is dropped rather than half-applied over the new ones. The next
// save from the editor replaces it outright.
const SUPERSEDED_SAVE_KEYS = {
  shipmentAdvice: ['ref_line1', 'goods_line', 'closing_line'],
  // The truck operators' letter was printed on the non-negotiable letter's
  // RE-block-and-bullets rows until it was given its own certification
  // paragraph, so a blob still carrying those rows is dropped outright.
  truckOperators: ['re_content', 'cert_text', 'bullet1', 'bullet2', 'bullet3'],
};

export function resolvePdfData(document, company, authority, docType, savedOverride) {
  const schema = PDF_EDIT_SCHEMAS[docType];
  if (!schema) return {};
  const defaults = schema.buildDefaults(document || {}, company || {}, authority || null);
  const saved = savedOverride !== undefined ? savedOverride : getSavedPdfData(document, docType);
  if (!saved) return defaults;
  const superseded = SUPERSEDED_SAVE_KEYS[docType] || [];
  if (superseded.some((key) => saved[key] !== undefined)) return defaults;
  const fixed = new Set(fixedFieldKeys(docType));
  const own = new Set(ownFieldKeys(docType));
  const out = { ...defaults };
  for (const key of Object.keys(defaults)) {
    if (fixed.has(key)) continue; // the invoice writes this one, an edit never does
    const v = saved[key];
    if (v === undefined || v === null) continue;
    // An emptied box means "follow the invoice" everywhere except the blocks
    // the exporter owns, where it means the paragraph is not wanted at all.
    if (String(v) === '' && !own.has(key)) continue;
    out[key] = String(v);
  }
  return out;
}
